/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RiskSignalTopUser,
  RiskSignalTopUser$inboundSchema,
} from "./risksignaltopuser.js";

/**
 * Severity rating derived from the risk score.
 */
export const RiskSignalSeverity = {
  Low: "low",
  Medium: "medium",
  High: "high",
  Critical: "critical",
} as const;
/**
 * Severity rating derived from the risk score.
 */
export type RiskSignalSeverity = OpenEnum<typeof RiskSignalSeverity>;

/**
 * One clustered risk signal: all live findings for a single rule in the window, with a heuristic severity score and trend/spread stats.
 */
export type RiskSignal = {
  /**
   * Source apps (chat surfaces) the findings were observed in. Empty until app attribution is recorded on findings.
   */
  apps: Array<string>;
  /**
   * Canonical risk category of the rule (secrets, pii, ...).
   */
  category: string;
  /**
   * Representative finding description for this rule. Empty when findings carry no description.
   */
  description: string;
  /**
   * Detector sources that produced findings in this signal (gitleaks, presidio, prompt_injection, ...).
   */
  detectionSources: Array<string>;
  /**
   * Deduplicated finding count in the window.
   */
  findings: number;
  /**
   * Event time of the earliest finding in the window.
   */
  firstSeen: Date;
  /**
   * Stable client identity for the signal. Currently 'rule:<rule_id>'.
   */
  key: string;
  /**
   * Event time of the latest finding in the window.
   */
  lastSeen: Date;
  /**
   * Concrete MCP server IDs the findings in this signal were observed on. Empty when no finding carries server attribution.
   */
  mcpServerIds: Array<string>;
  /**
   * Finding count in the equal-length window immediately before from.
   */
  previousFindings: number;
  /**
   * Heuristic severity score on the 0.1-10 scale used across risk surfaces.
   */
  riskScore: number;
  /**
   * Rule identifier the signal clusters on (e.g. 'secret.aws_access_token', 'pii.email_address').
   */
  ruleId: string;
  /**
   * Severity rating derived from the risk score.
   */
  severity: RiskSignalSeverity;
  /**
   * Deduplicated finding counts per equal-width time bucket across the window, oldest bucket first. Powers the per-signal trend sparkline.
   */
  sparkline: Array<number>;
  /**
   * Distinct teams with at least one finding in the window. Zero until team attribution is recorded on findings.
   */
  teams: number;
  /**
   * Concrete tool names the findings in this signal were observed on. Empty when no finding carries tool attribution.
   */
  toolNames: Array<string>;
  /**
   * Top users by finding count within the signal.
   */
  topUsers: Array<RiskSignalTopUser>;
  /**
   * Distinct users with at least one finding in the window.
   */
  users: number;
};

/** @internal */
export const RiskSignalSeverity$inboundSchema: z.ZodMiniType<
  RiskSignalSeverity,
  unknown
> = openEnums.inboundSchema(RiskSignalSeverity);

/** @internal */
export const RiskSignal$inboundSchema: z.ZodMiniType<RiskSignal, unknown> = z
  .pipe(
    z.object({
      apps: z.array(z.string()),
      category: z.string(),
      description: z.string(),
      detection_sources: z.array(z.string()),
      findings: z.int(),
      first_seen: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      key: z.string(),
      last_seen: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      mcp_server_ids: z.array(z.string()),
      previous_findings: z.int(),
      risk_score: z.number(),
      rule_id: z.string(),
      severity: RiskSignalSeverity$inboundSchema,
      sparkline: z.array(z.int()),
      teams: z.int(),
      tool_names: z.array(z.string()),
      top_users: z.array(RiskSignalTopUser$inboundSchema),
      users: z.int(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "detection_sources": "detectionSources",
        "first_seen": "firstSeen",
        "last_seen": "lastSeen",
        "mcp_server_ids": "mcpServerIds",
        "previous_findings": "previousFindings",
        "risk_score": "riskScore",
        "rule_id": "ruleId",
        "tool_names": "toolNames",
        "top_users": "topUsers",
      });
    }),
  );

export function riskSignalFromJSON(
  jsonString: string,
): SafeParseResult<RiskSignal, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskSignal$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskSignal' from JSON`,
  );
}
